import { ColorMatrixFilter, Graphics, Sprite, Text, type Texture } from 'pixi.js';
import { BoardDialog } from '../board/BoardDialog';
import { BrassButton } from '../board/BrassButton';
import { animate, COLOURS, displayStyle, prefersReducedMotion, uiStyle } from '../board/ui';
import { glowTexture } from '../road/glow';
import { offerLines, type WorldOfferState } from './worldOffer';

export { offerLines, type WorldOfferState } from './worldOffer';

/** The world offer board's art, looked up when it opens. */
export interface WorldOfferArt {
  floor: () => Texture | null;
  /** The world's round painting (as on the worlds list). */
  hero: () => Texture | null;
}

/** A world offer as the host sees it. */
export interface WorldOfferView {
  /** The world's name. */
  name: string;
  /** How many of its games the offer opens (the first is always open). */
  games: number;
  startsAt: number;
  endsAt: number;
  state: WorldOfferState;
  /** Takes the offer on the server; the new state, or null when nothing changed. */
  claim: () => Promise<WorldOfferState | null>;
}

/**
 * A world's games free for a week, from a Facebook post (producer
 * 2026-11-04: "Time sensitive offer. It has to feel like something they
 * will miss out on"). The world's painting, what it opens and when it
 * ends. OPEN THE WORLD takes it; then GO THERE goes to the world's map.
 * After the deadline it says the offer has ended. Resolves with "go"
 * when the player chose to go there.
 */
export class WorldOfferBoard extends BoardDialog {
  private readonly glow = new Sprite(glowTexture());
  private readonly hero = new Sprite();
  /** The painting is round, as on the worlds list. */
  private readonly heroMask = new Graphics();
  private readonly heroRing = new Graphics();
  private readonly line: Text;
  private readonly small: Text;
  private readonly mainButton: BrassButton;
  private readonly later: Text;
  private view: WorldOfferView | null = null;
  private state: WorldOfferState = 'open';
  private busy = false;
  private done: ((go: boolean) => void) | null = null;
  private arranging = false;

  constructor(private readonly art: WorldOfferArt) {
    super({ title: 'THIS WEEK ONLY', floor: art.floor(), maxWidth: 420, titleSize: 18 });
    this.glow.anchor.set(0.5);
    this.glow.blendMode = 'add';
    this.glow.tint = 0xbfe6ff;
    this.hero.anchor.set(0.5);
    this.hero.mask = this.heroMask;
    this.line = new Text({ text: '', style: { ...displayStyle(24), align: 'center', wordWrap: true } });
    this.line.anchor.set(0.5, 0);
    this.small = new Text({ text: '', style: { ...uiStyle(15, COLOURS.cream), align: 'center', wordWrap: true } });
    this.small.anchor.set(0.5, 0);
    this.mainButton = new BrassButton({ label: 'OPEN THE WORLD', kind: 'gold', size: 18, onTap: () => void this.act() });
    this.later = new Text({ text: 'Not now', style: uiStyle(14, COLOURS.cream, '600') });
    this.later.anchor.set(0.5, 0);
    this.later.eventMode = 'static';
    this.later.cursor = 'pointer';
    this.later.on('pointertap', () => void this.finish(false));
    this.content.addChild(this.glow, this.hero, this.heroMask, this.heroRing, this.line, this.small, this.mainButton, this.later);
  }

  /** The board as plain data, for the browser tests. */
  get snapshot(): { open: boolean; state: WorldOfferState; title: string; line: string; small: string; buttons: string[] } {
    return {
      open: this.visible,
      state: this.state,
      title: this.titleText,
      line: this.line.text,
      small: this.small.text,
      buttons: this.visible ? [this.mainButton.labelText, ...(this.later.visible ? ['Not now'] : [])] : [],
    };
  }

  /** Press a button by its label (browser tests). */
  press(label: string): boolean {
    if (!this.visible) return false;
    if (label === 'Not now' && this.later.visible) {
      void this.finish(false);
      return true;
    }
    if (this.mainButton.labelText !== label) return false;
    void this.act();
    return true;
  }

  /** Show the offer; resolves true when the player chose to go to the world. */
  async play(view: WorldOfferView): Promise<boolean> {
    this.view = view;
    this.busy = false;
    this.setFloor(this.art.floor());
    const hero = this.art.hero();
    if (hero) this.hero.texture = hero;
    this.hero.visible = this.heroRing.visible = hero !== null;
    this.show(view.state);
    const closed = new Promise<boolean>((resolve) => (this.done = resolve));
    await this.open();
    this.shimmer();
    return closed;
  }

  layout(viewWidth: number, viewHeight: number): void {
    super.layout(viewWidth, viewHeight);
    this.arrange();
  }

  private show(state: WorldOfferState): void {
    const view = this.view;
    if (!view) return;
    this.state = state;
    const words = offerLines(state, view.name, view.games, view.startsAt, view.endsAt, Date.now() / 1000);
    this.setTitle(words.title);
    this.line.text = words.line;
    this.small.text = words.small;
    this.mainButton.setLabel(words.button);
    this.mainButton.setEnabled(true);
    this.later.visible = state === 'open' || state === 'taken';
    // An ended offer's world shows in grey: the thing they missed.
    this.hero.filters = state === 'ended' ? [greyed()] : [];
    this.glow.visible = state === 'open' || state === 'taken';
    this.arrange();
  }

  private async act(): Promise<void> {
    const view = this.view;
    if (!view || this.busy) return;
    if (this.state === 'open') {
      this.busy = true;
      this.mainButton.setEnabled(false);
      const next = await view.claim();
      this.busy = false;
      if (!next) {
        this.mainButton.setEnabled(true);
        return;
      }
      this.show(next);
      if (next === 'taken') void this.celebrate({ x: this.contentWidth / 2, y: this.hero.y });
      return;
    }
    await this.finish(this.state === 'taken');
  }

  private async finish(go: boolean): Promise<void> {
    const done = this.done;
    this.done = null;
    await this.close();
    done?.(go);
  }

  /** The painting's glow breathes while the offer waits. */
  private shimmer(): void {
    if (prefersReducedMotion()) return;
    const loop = () => {
      if (!this.visible || this.destroyed || !this.glow.visible) return;
      void animate(1600, (t) => (this.glow.alpha = 0.45 + 0.4 * Math.sin(t * Math.PI))).then(loop);
    };
    loop();
  }

  private arrange(): void {
    if (this.arranging || !this.later) return;
    this.arranging = true;
    const w = this.contentWidth;
    let y = 4;
    if (this.hero.visible) {
      const size = Math.min(200, w * 0.6);
      this.hero.scale.set(size / Math.max(1, this.hero.texture.width, this.hero.texture.height));
      this.hero.position.set(w / 2, y + size / 2);
      this.glow.width = this.glow.height = size * 1.6;
      this.glow.position.copyFrom(this.hero.position);
      this.heroMask.clear().circle(w / 2, y + size / 2, size / 2).fill({ color: 0xffffff });
      this.heroRing.clear().circle(w / 2, y + size / 2, size / 2).stroke({ color: COLOURS.brass, width: 4 });
      y += size + 14;
    }
    for (const t of [this.line, this.small]) {
      t.style.wordWrapWidth = w - 8;
      t.position.set(w / 2, y);
      y += t.height + 10;
    }
    const bw = Math.min(w, 260);
    this.mainButton.resize(bw, 50);
    this.mainButton.position.set((w - bw) / 2, y + 4);
    y += 62;
    if (this.later.visible) {
      this.later.position.set(w / 2, y);
      y += this.later.height + 6;
    }
    this.setContentHeight(y);
    this.arranging = false;
  }
}

function greyed(): ColorMatrixFilter {
  const f = new ColorMatrixFilter();
  f.desaturate();
  f.brightness(0.75, true);
  return f;
}
